import { useState, useEffect, useCallback } from "react";
import { auth } from "@canva/user";
import { requestOpenExternalUrl } from "@canva/platform";
import { api, type LinkedOrg } from "../api/client";

export type AuthState =
  | { status: "loading" }
  | { status: "unlinked" }
  | { status: "linking" }
  | {
      status: "linked";
      orgId: string;
      orgName: string;
      orgs: LinkedOrg[];
    }
  | { status: "error"; message: string };

declare const BACKEND_HOST: string;

/**
 * Manages Canva → Profitual authentication with multi-org support.
 *
 * On mount: getCanvaUserToken → /auth/exchange (returns full linked-org list).
 * If there's a remembered selection in localStorage that's still linked we
 * pick it; otherwise pick the first linked org. `selectOrg` switches between
 * linked orgs by re-issuing the Profitual token via /auth/select-org.
 */

const LAST_ORG_STORAGE_KEY = "profitual:canva:last-org";

function loadLastOrg(canvaUserId: string): string | null {
  try {
    const raw = localStorage.getItem(LAST_ORG_STORAGE_KEY);
    if (!raw) return null;
    const byUser = JSON.parse(raw) as Record<string, string>;
    return byUser[canvaUserId] ?? null;
  } catch {
    return null;
  }
}

function saveLastOrg(canvaUserId: string, orgId: string): void {
  try {
    const raw = localStorage.getItem(LAST_ORG_STORAGE_KEY);
    const byUser = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    byUser[canvaUserId] = orgId;
    localStorage.setItem(LAST_ORG_STORAGE_KEY, JSON.stringify(byUser));
  } catch {
    // Non-fatal — iframe localStorage quirks
  }
}

function parseJwtSub(token: string): string | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]!));
    return (payload.sub as string) ?? (payload.userId as string) ?? null;
  } catch {
    return null;
  }
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({ status: "loading" });
  const [canvaToken, setCanvaToken] = useState<string | null>(null);

  /**
   * Issue a Profitual token for a linked org and move to the `linked` state.
   * Used both by the initial-mount auto-pick and the user-triggered switcher.
   */
  const activateOrg = useCallback(
    async (token: string, orgId: string, allOrgs: LinkedOrg[]) => {
      const res = await api.selectOrg(token, orgId);
      api.setToken(res.token);
      const canvaUserId = parseJwtSub(token);
      if (canvaUserId) saveLastOrg(canvaUserId, res.orgId);
      setState({
        status: "linked",
        orgId: res.orgId,
        orgName: res.orgName,
        orgs: allOrgs,
      });
    },
    []
  );

  // Exchange Canva JWT on mount → pick an org
  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const token = await auth.getCanvaUserToken();
        if (cancelled) return;
        setCanvaToken(token);

        const res = await api.exchange(token);
        if (cancelled) return;

        if (res.status !== "linked" || res.orgs.length === 0) {
          setState({ status: "unlinked" });
          return;
        }

        // Prefer the last-used org if it's still in the linked list; else first.
        const canvaUserId = parseJwtSub(token);
        const remembered = canvaUserId ? loadLastOrg(canvaUserId) : null;
        const rememberedOrg = remembered
          ? res.orgs.find((o) => o.orgId === remembered)
          : undefined;
        const target = rememberedOrg ?? res.orgs[0];

        await activateOrg(token, target.orgId, res.orgs);
      } catch (err) {
        if (!cancelled) {
          setState({
            status: "error",
            message: err instanceof Error ? err.message : "Authentication failed",
          });
        }
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, [activateOrg]);

  /**
   * Open the Profitual login page and poll until /auth/exchange returns a
   * larger org list than we started with (i.e. a new link completed).
   */
  const connect = useCallback(async () => {
    if (!canvaToken) return;

    // Snapshot current link count so polling can tell when a new link appears.
    let beforeCount = 0;
    try {
      const before = await api.exchange(canvaToken);
      beforeCount = before.orgs.length;
    } catch {
      beforeCount = 0;
    }

    setState({ status: "linking" });

    try {
      const loginUrl = `${BACKEND_HOST}/canva/login?canvaToken=${encodeURIComponent(canvaToken)}`;
      await requestOpenExternalUrl({ url: loginUrl });

      const maxAttempts = 150; // 5 minutes at 2s intervals
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise((r) => setTimeout(r, 2000));

        try {
          const freshToken = await auth.getCanvaUserToken();
          const res = await api.exchange(freshToken);

          if (res.status === "linked" && res.orgs.length > beforeCount) {
            setCanvaToken(freshToken);
            // Newly linked row is appended, pick it so the user lands on what they just connected.
            const newOrg = res.orgs[res.orgs.length - 1];
            await activateOrg(freshToken, newOrg.orgId, res.orgs);
            return;
          }
        } catch {
          // Ignore individual poll errors
        }
      }

      setState({ status: "error", message: "Login timed out. Please try again." });
    } catch (err) {
      setState({
        status: "error",
        message: err instanceof Error ? err.message : "Connection failed",
      });
    }
  }, [canvaToken, activateOrg]);

  /** Switch to another linked org (re-issues token; caller re-renders data-fetching hooks). */
  const selectOrg = useCallback(
    async (orgId: string) => {
      if (!canvaToken) return;
      if (state.status !== "linked") return;
      if (orgId === state.orgId) return;
      try {
        await activateOrg(canvaToken, orgId, state.orgs);
      } catch (err) {
        setState({
          status: "error",
          message: err instanceof Error ? err.message : "Failed to switch organization",
        });
      }
    },
    [canvaToken, state, activateOrg]
  );

  /** Disconnect the active org only — other linked orgs remain accessible. */
  const disconnect = useCallback(async () => {
    if (state.status !== "linked") return;
    try {
      await api.disconnect();
    } catch {
      // Best-effort
    }
    api.clearToken();

    const remaining = state.orgs.filter((o) => o.orgId !== state.orgId);
    if (remaining.length === 0 || !canvaToken) {
      setState({ status: "unlinked" });
      return;
    }
    try {
      await activateOrg(canvaToken, remaining[0].orgId, remaining);
    } catch {
      setState({ status: "unlinked" });
    }
  }, [canvaToken, state, activateOrg]);

  return { state, connect, disconnect, selectOrg };
}
